/* eslint-disable no-console */

// Decrypt timing, to compare the two encryption schemes (nc1 vs Legacy/Virgil)
// on real screenshots. DEV / DEBUG ONLY: active on a non-prod build, or on a
// prod build after `localStorage.debug = 'true'` (the same switch that
// un-silences the console - see main.js). Otherwise `start()` returns null and
// the call sites skip everything.
//
// Every decrypt is split in two, because only one half is the scheme's own
// cost:
//   crypto  parsing, signature check and decryption of THIS record - measured
//           around exactly those calls (cryptoBegin/cryptoEnd).
//   setup   the rest of the call (total - crypto): reading the blob into
//           memory and waiting for what the decrypt needs - nc1: the verified
//           account context (chain, device list, epoch secrets; built once,
//           then cached); Legacy: E3Kit init + the sender's card (cached too).
//           Dominated by the network on the first record, ~0 afterwards.
//
// Kinds: 'thumbnail' and 'fullsize' (ImageCache tells them apart by the
// `.thumbnail` object key in the URL), 'image' when the caller did not say,
// and 'string'. Each gets its own summary row per scheme - a 10 KB thumbnail
// and a full screenshot have nothing in common.
//
// Reading the numbers: nc1's crypto half is synchronous, so its figure is
// exact. Legacy decrypts a file in awaited chunks, so whenever anything else
// runs in between (another decrypt, a base64 conversion, a render) that time
// lands in ITS crypto figure: a gallery of thumbnails gives Legacy a fine
// median and a p95 / mean dozens of times higher. The `solo` columns only
// use decrypts during which no other decrypt was in flight at any point -
// compare those, by median and by ms/MB on full-size images. (They still
// cannot see non-decrypt work on the main thread, so Legacy can remain
// slightly inflated even there.)
//
// What is printed: one line per FULL-SIZE image that decrypted. Thumbnails
// (hundreds per page), strings (four per event) and failures only count in
// the summary - a gallery used to print a line per thumbnail, which buried
// everything else. The summary prints by itself once the decrypts go quiet,
// and on demand:
//   __decryptTiming.summary()        print the table now
//   __decryptTiming.reset()          start a fresh comparison
//   __decryptTiming.samples          the raw samples (every call, always)
//   __decryptTiming.verbose = true   one line for EVERY call, thumbnails and failures included

const SUMMARY_QUIET_MS = 2000
const MAX_SAMPLES = 5000 // a long dev session must not grow without bound; the oldest go first
const DETAIL_MIN_BYTES = 64 * 1024 // only for kind 'image' (caller did not say): thumbnails are 5-20 KB
const TAG = '[decrypt-timing]'

function isEnabled () {
  try {
    if (process.env.VUE_APP_AWS_ENV !== 'prod') return true
    return typeof localStorage !== 'undefined' && localStorage.getItem('debug') === 'true'
  } catch (e) {
    return false
  }
}

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())
const ms = (n) => (n == null ? '-' : n.toFixed(1))
const kb = (bytes) => (bytes == null ? '-' : (bytes / 1024).toFixed(0))

let samples = []
let inFlight = 0
let startSeq = 0
let summaryTimer = null
let verbose = false

function quantile (sorted, q) {
  if (!sorted.length) return null
  const i = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1))
  return sorted[i]
}

function stats (values) {
  const sorted = values.filter((v) => v != null).sort((a, b) => a - b)
  if (!sorted.length) return { median: null, mean: null, p95: null, min: null, max: null }
  return {
    median: quantile(sorted, 0.5),
    mean: sorted.reduce((a, b) => a + b, 0) / sorted.length,
    p95: quantile(sorted, 0.95),
    min: sorted[0],
    max: sorted[sorted.length - 1],
  }
}

function summarize () {
  const groups = new Map()
  for (const s of samples) {
    const key = `${s.scheme} ${s.kind}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(s)
  }
  const rows = []
  for (const [key, list] of [...groups].sort()) {
    const ok = list.filter((s) => s.ok)
    const total = stats(ok.map((s) => s.totalMs))
    const crypto = stats(ok.map((s) => s.cryptoMs))
    const setup = stats(ok.map((s) => s.setupMs))
    // solo = nothing else was decrypting at any point during the call (see the header).
    const solo = ok.filter((s) => !s.contended)
    const soloCrypto = stats(solo.map((s) => s.cryptoMs))
    const soloBytes = solo.reduce((a, s) => a + (s.bytes || 0), 0)
    const soloCryptoSum = solo.reduce((a, s) => a + (s.cryptoMs || 0), 0)
    rows.push({
      'scheme kind': key,
      n: ok.length,
      failed: list.length - ok.length,
      'median KB': kb(quantile(ok.map((s) => s.bytes).filter((b) => b != null).sort((a, b) => a - b), 0.5)),
      'crypto median': ms(crypto.median),
      'crypto p95': ms(crypto.p95),
      'solo n': solo.length,
      'solo median': ms(soloCrypto.median),
      'solo p95': ms(soloCrypto.p95),
      // Throughput of the crypto half over the uncontended decrypts: the fairest single number.
      'solo ms/MB': soloBytes > 0 ? ms(soloCryptoSum / (soloBytes / (1024 * 1024))) : '-',
      'setup median': ms(setup.median),
      'setup max': ms(setup.max),
      'total median': ms(total.median),
    })
  }
  return rows
}

function printSummary () {
  const rows = summarize()
  if (!rows.length) {
    console.log(`${TAG} nothing timed yet`)
    return rows
  }
  // Plain text (not console.table) so it also survives the console capture and remote consoles.
  const cols = Object.keys(rows[0])
  const width = cols.map((c) => Math.max(c.length, ...rows.map((r) => String(r[c]).length)))
  const line = (cells) => cells.map((c, i) => String(c).padEnd(width[i])).join('  ')
  console.log([`${TAG} summary (times in ms, successful decrypts only; solo = no other decrypt in flight during the call)`, line(cols), ...rows.map((r) => line(cols.map((c) => r[c])))].join('\n'))
  return rows
}

function scheduleSummary () {
  if (summaryTimer) clearTimeout(summaryTimer)
  summaryTimer = setTimeout(() => {
    summaryTimer = null
    if (inFlight === 0) printSummary()
    else scheduleSummary()
  }, SUMMARY_QUIET_MS)
}

/**
 * Start timing one decrypt. `kind` is 'thumbnail' | 'fullsize' | 'image' |
 * 'string'. Returns null when timing is off, so call sites guard every use
 * with `if (timing)`.
 *   t.note(scheme, bytes)          which scheme this record turned out to be
 *                                  ('nc1' | 'legacy') and its ciphertext size
 *   t.cryptoBegin() / cryptoEnd()  around the actual parse / verify / decrypt
 *   t.done() / t.fail()            finished; a call that never reached
 *                                  note() (nothing to decrypt) is dropped
 */
export function start (kind) {
  if (!isEnabled()) return null
  const startedAt = now()
  const concurrent = ++inFlight
  const seq = ++startSeq
  let scheme = null
  let bytes = null
  let cryptoMs = 0
  let cryptoStartedAt = null
  let finished = false
  const cryptoEnd = () => {
    if (cryptoStartedAt == null) return
    cryptoMs += now() - cryptoStartedAt
    cryptoStartedAt = null
  }
  const finish = (ok) => {
    if (finished) return
    finished = true
    inFlight -= 1
    cryptoEnd()
    if (scheme == null) return
    const totalMs = now() - startedAt
    // Something else was decrypting when this started, or started before it finished.
    const contended = concurrent > 1 || startSeq !== seq
    const sample = { scheme, kind, ok, bytes, totalMs, setupMs: totalMs - cryptoMs, cryptoMs, concurrent, contended }
    samples.push(sample)
    if (samples.length > MAX_SAMPLES) samples.splice(0, samples.length - MAX_SAMPLES)
    const detail = ok && (kind === 'fullsize' || (kind === 'image' && bytes != null && bytes >= DETAIL_MIN_BYTES))
    if (verbose || detail) {
      console.log(`${TAG} ${scheme} ${kind} ${ok ? 'ok' : 'FAILED'} total=${ms(sample.totalMs)}ms `
        + `setup=${ms(sample.setupMs)}ms crypto=${ms(sample.cryptoMs)}ms size=${kb(sample.bytes)}KB `
        + (contended ? `contended (${concurrent} in flight at start)` : 'solo'))
    }
    scheduleSummary()
  }
  return {
    note (s, b) { scheme = s; bytes = b == null ? null : b },
    cryptoBegin () { if (cryptoStartedAt == null) cryptoStartedAt = now() },
    cryptoEnd,
    done () { finish(true) },
    fail () { finish(false) },
  }
}

if (typeof window !== 'undefined' && isEnabled()) {
  window.__decryptTiming = {
    summary: printSummary,
    reset () { samples = []; console.log(`${TAG} reset`) },
    get samples () { return samples },
    get verbose () { return verbose },
    set verbose (v) { verbose = !!v },
  }
}
